import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { formatSearchString, isAdvertiser } from '~/helpers'
import useFetchSWR from '~/hooks/useFetchSWR'
import useQueryParams from '~/hooks/useQuery'
import { mapCheckingAccountOrigins } from '~/modules/retailMedia/components/WalletBalanceOrigin/mapOrigins'
import { normalizeCounterpartId } from '~/modules/retailMedia/helpers/isValidCounterpartId'
import { reatailmediaApi } from '~/modules/retailMedia/services/api'
import { buildWalletByPairParams } from '~/modules/retailMedia/services/checking-account/buildWalletByPairParams'
import { getWalletFromListResponse } from '~/modules/retailMedia/services/checking-account/getWalletFromListResponse'
import { useAppSelector } from '~/store/hooks'

const mapWalletUpdateInList = (
  rows: GetCheckingAccountWithAccountInfoResponse[],
  walletId: string,
  nextWallet: GetCheckingAccountWithAccountInfoResponse
) => rows.map(item => (item.id === walletId ? nextWallet : item))

export const useWalletByPair = () => {
  const { id: walletId } = useParams()
  const counterpartId = normalizeCounterpartId(useQueryParams().get('rmid'))
  const accountId = useAppSelector(state => state.signin.accountId)

  const listUrl = useMemo(() => {
    if (!counterpartId || !accountId) return null

    return `${reatailmediaApi}/checking-account?${formatSearchString(
      buildWalletByPairParams({
        counterpartId,
        isAdvertiserAccount: isAdvertiser()
      })
    )}`
  }, [accountId, counterpartId])

  const singleWalletUrl = useMemo(() => {
    if (counterpartId || !walletId) return null

    return `${reatailmediaApi}/checking-account/${walletId}?${formatSearchString(
      { account_info: true }
    )}`
  }, [counterpartId, walletId])

  const {
    data: listData,
    isValidating: isListValidating,
    mutate: mutateList
  } = useFetchSWR<PairCheckingAccountResponse>({
    url: listUrl ?? '',
    shouldFetch: Boolean(listUrl),
    refreshInterval: 60000,
    revalidateOnFocus: false
  })

  const {
    data: singleWallet,
    isValidating: isSingleValidating,
    mutate: mutateSingle
  } = useFetchSWR<GetCheckingAccountWithAccountInfoResponse>({
    url: singleWalletUrl ?? '',
    shouldFetch: Boolean(singleWalletUrl),
    refreshInterval: 60000,
    revalidateOnFocus: false
  })

  const wallet = useMemo(() => {
    if (listUrl) return getWalletFromListResponse(listData, walletId)
    if (singleWalletUrl) return singleWallet
    return undefined
  }, [listData, listUrl, singleWallet, singleWalletUrl, walletId])

  const mappedOrigins = useMemo(
    () => mapCheckingAccountOrigins(wallet?.origins),
    [wallet?.origins]
  )

  const mutateWallet = useCallback(
    async (updates: Partial<GetCheckingAccountWithAccountInfoResponse>) => {
      if (!wallet) return

      const nextWallet = { ...wallet, ...updates }

      if (listUrl && listData) {
        if (Array.isArray(listData)) {
          await mutateList(
            mapWalletUpdateInList(listData, wallet.id, nextWallet)
          )
          return
        }

        if (!Array.isArray(listData.data)) return

        await mutateList({
          ...listData,
          data: mapWalletUpdateInList(listData.data, wallet.id, nextWallet)
        })
        return
      }

      if (singleWalletUrl) {
        await mutateSingle(nextWallet)
      }
    },
    [listData, listUrl, mutateList, mutateSingle, singleWalletUrl, wallet]
  )

  const isValidating = isListValidating || isSingleValidating

  return {
    wallet,
    mappedOrigins,
    hasMappedOrigins: mappedOrigins.length > 0,
    isValidating,
    initialLoading: !wallet && isValidating,
    mutateWallet
  }
}
